import React, { useState } from 'react';
import { Box, TextField, Button, Divider, Typography, Alert } from '@mui/material';
import { Add } from '@mui/icons-material';

import { ChoiceAction } from '../../../../types';

import ChoiceItemEditor from './ChoiceItemEditor';
import PreviewSection from './PreviewSection';
import { ChoiceOption } from './types';

interface ChoiceActionEditorProps {
  action: ChoiceAction;
  onChange: (action: ChoiceAction) => void;
  contextKeys: string[];
}

const ChoiceActionEditor: React.FC<ChoiceActionEditorProps> = ({
  action,
  onChange,
  contextKeys,
}) => {
  const [newChoiceKey, setNewChoiceKey] = useState('');

  const handleChoiceIdChange = (choiceId: string) => {
    onChange({
      ...action,
      data: {
        ...action.data,
        choiceId,
      },
    });
  };

  const handleChoiceChange = (key: string, choice: ChoiceOption) => {
    onChange({
      ...action,
      data: {
        ...action.data,
        choices: {
          ...action.data.choices,
          [key]: choice,
        },
      },
    });
  };

  const handleChoiceDelete = (key: string) => {
    const newChoices = { ...action.data.choices };
    delete newChoices[key];
    onChange({
      ...action,
      data: {
        ...action.data,
        choices: newChoices,
      },
    });
  };

  const handleAddChoice = () => {
    if (!newChoiceKey.trim()) {
      return;
    }

    const sanitizedKey = newChoiceKey.trim().toLowerCase().replace(/\s+/g, '_');

    if (action.data.choices[sanitizedKey]) {
      alert('A choice with this key already exists');
      return;
    }

    onChange({
      ...action,
      data: {
        ...action.data,
        choices: {
          ...action.data.choices,
          [sanitizedKey]: {
            text: newChoiceKey,
            value: sanitizedKey,
          },
        },
      },
    });
    setNewChoiceKey('');
  };

  const choiceEntries = Object.entries(action.data.choices || {});
  const hasChoices = choiceEntries.length > 0;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Choice Action Configuration
      </Typography>

      <Box mb={3}>
        <TextField
          label="Context Key (choiceId)"
          value={action.data.choiceId || ''}
          onChange={(e) => handleChoiceIdChange(e.target.value)}
          fullWidth
          required
          helperText="The key where the selected value will be stored in context"
        />
        {contextKeys.length > 0 && (
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            Available context keys: {contextKeys.join(', ')}
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" gutterBottom>
        Choices
      </Typography>

      {!hasChoices && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No choices defined yet. Add at least one choice option.
        </Alert>
      )}

      {choiceEntries.map(([key, choice]) => (
        <ChoiceItemEditor
          key={key}
          choiceKey={key}
          choice={choice}
          onChange={handleChoiceChange}
          onDelete={handleChoiceDelete}
          canDelete={choiceEntries.length > 1}
        />
      ))}

      <Box display="flex" gap={2} mb={3}>
        <TextField
          label="New Choice Key"
          value={newChoiceKey}
          onChange={(e) => setNewChoiceKey(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleAddChoice();
            }
          }}
          placeholder="e.g., 'upbeat' or 'professional'"
          fullWidth
        />
        <Button
          variant="contained"
          onClick={handleAddChoice}
          startIcon={<Add />}
          disabled={!newChoiceKey.trim()}
        >
          Add Choice
        </Button>
      </Box>

      <Divider sx={{ my: 3 }} />

      <PreviewSection choices={action.data.choices || {}} />
    </Box>
  );
};

export default ChoiceActionEditor;
